import type { ApiError } from '@/types'
import { clearSession, getToken } from './session'

const BASE = '/api'

/** Evento global de sesión inválida → el AuthProvider desloguea y manda a /login. */
export const UNAUTHORIZED_EVENT = 'ps:unauthorized'

export class RequestError extends Error {
  status: number
  code?: string
  /** Payload extra de algunos errores (ej: el detalle por recurso del probe de VTEX). */
  probe?: unknown
  constructor(status: number, message: string, code?: string, probe?: unknown) {
    super(message)
    this.status = status
    this.code = code
    this.probe = probe
  }
}

/** Manejo común de respuesta: 401 → deslogueo global, error tipado, parseo JSON. */
async function handleResponse<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    clearSession()
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
    throw new RequestError(401, 'Sesión expirada. Ingresá de nuevo.', 'UNAUTHENTICATED')
  }

  if (!res.ok) {
    let message = res.statusText
    let code: string | undefined
    let probe: unknown
    try {
      const body = (await res.json()) as ApiError & { probe?: unknown }
      message = body.error || message
      code = body.code
      probe = body.probe
    } catch {
      /* respuesta sin JSON */
    }
    throw new RequestError(res.status, message, code, probe)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken()
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'same-origin' })
  return handleResponse<T>(res)
}

/** Sube un archivo vía multipart/form-data. NO fija Content-Type a mano: el
 *  browser agrega el boundary correcto. Reutiliza el manejo de errores/401. */
async function upload<T>(path: string, file: File, field = 'file'): Promise<T> {
  const token = getToken()
  const headers = new Headers({ Accept: 'application/json' })
  if (token) headers.set('Authorization', `Bearer ${token}`)
  const form = new FormData()
  form.append(field, file)

  const res = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: form, credentials: 'same-origin' })
  return handleResponse<T>(res)
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload,
}
